import { ModuleLearnCore } from '@/components/learning/module-learn-core'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { categoryColors, categoryLabels, difficultyColors } from '@/constants/learning'
import { useTopic } from '@/hooks/use-learning'
import { cn } from '@/lib/utils'
import type { LearningModuleWithProgress, LearningStatus } from '@/types'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  Circle,
  Clock,
  Loader2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

function ModuleProgressIcon({ status, percent }: { status: LearningStatus; percent: number }) {
  if (status === 'completed') {
    return <CheckCircle className="h-5 w-5 text-green-600 shrink-0" aria-hidden />
  }
  if (status === 'not_started') {
    return <Circle className="h-5 w-5 text-muted-foreground/70 shrink-0" aria-hidden />
  }
  const r = 7
  const c = 2 * Math.PI * r
  const pct = Math.min(Math.max(percent, 8), 100)
  const dash = c * (pct / 100)
  return (
    <svg
      className="h-5 w-5 shrink-0 -rotate-90"
      viewBox="0 0 18 18"
      role="img"
      aria-label={`${percent}% complete`}
    >
      <title>{`${percent}% complete`}</title>
      <circle
        cx="9"
        cy="9"
        r={r}
        fill="none"
        className="stroke-muted-foreground/25"
        strokeWidth="2"
      />
      <circle
        cx="9"
        cy="9"
        r={r}
        fill="none"
        className="stroke-blue-600"
        strokeWidth="2"
        strokeDasharray={`${dash} ${c}`}
        strokeLinecap="round"
      />
    </svg>
  )
}

function ModulePicker({
  modules,
  selectedId,
  onSelect,
}: {
  modules: LearningModuleWithProgress[]
  selectedId: number
  onSelect: (id: number) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selectedIndex = modules.findIndex((m) => m.id === selectedId)
  const selected = modules[selectedIndex]

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div className="relative z-10 w-full" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex w-full items-center gap-3 rounded-xl border-0 bg-white px-4 py-3 text-left shadow-md ring-1 ring-black/5 transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-muted-foreground">
            Module {selectedIndex >= 0 ? selectedIndex + 1 : '—'}
          </p>
          <p className="truncate font-semibold text-foreground">
            {selected?.title ?? 'Select module'}
          </p>
        </div>
        <ChevronDown
          className={cn(
            'h-5 w-5 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-full overflow-visible rounded-xl border-0 bg-white py-1 shadow-lg ring-1 ring-black/10">
          {modules.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                onSelect(m.id)
                setOpen(false)
              }}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/60',
                m.id === selectedId && 'bg-blue-50/80',
              )}
            >
              <ModuleProgressIcon status={m.status} percent={m.progress_percent} />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-muted-foreground">Module {i + 1}</p>
                <p className="truncate font-semibold text-foreground">{m.title}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function LearnTopicPage() {
  const { topicId } = useParams<{ topicId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const pageScrollRef = useRef<HTMLDivElement>(null)

  const parsedTopicId = topicId ? Number(topicId) : Number.NaN
  const validTopicId = Number.isFinite(parsedTopicId) ? parsedTopicId : Number.NaN

  const { data: topic, isLoading, isError } = useTopic(validTopicId)

  const moduleParam = searchParams.get('module')
  const moduleFromUrl = moduleParam ? Number(moduleParam) : Number.NaN

  const sortedModules = useMemo(
    () => (topic?.modules ? [...topic.modules].sort((a, b) => a.order_index - b.order_index) : []),
    [topic?.modules],
  )

  const selectedModuleId = useMemo(() => {
    if (Number.isFinite(moduleFromUrl) && sortedModules.some((m) => m.id === moduleFromUrl)) {
      return moduleFromUrl
    }
    return sortedModules[0]?.id ?? Number.NaN
  }, [moduleFromUrl, sortedModules])

  useEffect(() => {
    if (!topic || sortedModules.length === 0) return
    const urlOk =
      Number.isFinite(moduleFromUrl) && sortedModules.some((m) => m.id === moduleFromUrl)
    if (urlOk) return
    const fallback =
      sortedModules.find((m) => m.status === 'in_progress') ??
      sortedModules.find((m) => m.status === 'not_started') ??
      sortedModules[0]
    if (fallback) {
      setSearchParams({ module: String(fallback.id) }, { replace: true })
    }
  }, [topic, sortedModules, moduleFromUrl, setSearchParams])

  const setModuleInUrl = useCallback(
    (id: number) => {
      setSearchParams({ module: String(id) })
    },
    [setSearchParams],
  )

  const isGenerating =
    topic?.generation_status === 'pending' || topic?.generation_status === 'generating'
  const isFailed = topic?.generation_status === 'failed'

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F5F5F5]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F5F5F5]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <p className="text-muted-foreground">Failed to load topic. Please try again later.</p>
        </div>
      </div>
    )
  }

  if (!topic) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F5F5F5]">
        <p className="text-muted-foreground">Topic not found</p>
      </div>
    )
  }

  return (
    <div ref={pageScrollRef} className="h-full overflow-y-auto bg-[#F5F5F5]">
      <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 mb-4 text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/learn')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Learn
        </Button>

        <header className="mb-2">
          <h1 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
            {topic.title}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {topic.description}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${categoryColors[topic.skill_category]}`}
            >
              {categoryLabels[topic.skill_category]}
            </span>
            <span
              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${difficultyColors[topic.difficulty]}`}
            >
              {topic.difficulty}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {topic.estimated_hours} hours
            </span>
          </div>
          {!isGenerating && (
            <div className="mt-4 space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {topic.completed_modules}/{topic.modules_count} modules completed
                </span>
                <span className="font-medium tabular-nums">{topic.progress_percent}%</span>
              </div>
              <div
                className="h-2 w-full max-w-md rounded-full bg-white shadow-inner ring-1 ring-black/5"
                aria-label={`${Math.min(topic.progress_percent, 100)}% complete`}
              >
                <div
                  className="h-2 rounded-full bg-blue-600 transition-all"
                  style={{ width: `${Math.min(topic.progress_percent, 100)}%` }}
                />
              </div>
            </div>
          )}
        </header>

        {isGenerating && (
          <div
            className="relative z-10 mt-3 flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border-0 bg-white px-4 py-12 text-center shadow-md ring-1 ring-black/5 sm:px-8"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="h-9 w-9 shrink-0 animate-spin text-blue-600" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold text-foreground">Curating topics to address skill gaps</p>
              <p className="max-w-md text-sm text-muted-foreground">
                Modules for this skill will appear here when ready. This usually takes about 30–60
                seconds.
              </p>
            </div>
          </div>
        )}

        {isFailed && (
          <Card className="mt-6 border-destructive/30 bg-destructive/5">
            <CardContent className="py-8 text-center">
              <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
              <h3 className="mb-2 font-semibold">Generation failed</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {topic.generation_error || 'Something went wrong while generating content.'}
              </p>
              <Button variant="outline" onClick={() => navigate('/learn')}>
                Back to Learn
              </Button>
            </CardContent>
          </Card>
        )}

        {!isGenerating && !isFailed && sortedModules.length === 0 && (
          <Card className="mt-6">
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No modules available yet.</p>
            </CardContent>
          </Card>
        )}

        {!isGenerating &&
          !isFailed &&
          sortedModules.length > 0 &&
          Number.isFinite(selectedModuleId) && (
            <>
              <div className="relative z-20 pt-2">
                <ModulePicker
                  modules={sortedModules}
                  selectedId={selectedModuleId}
                  onSelect={setModuleInUrl}
                />
              </div>

              <div className="relative mt-3 rounded-xl border-0 bg-white px-4 py-6 shadow-md ring-1 ring-black/5 sm:px-6 sm:py-8 md:px-8">
                <ModuleLearnCore
                  topicId={validTopicId}
                  moduleId={selectedModuleId}
                  onModuleChange={setModuleInUrl}
                  scrollContainerRef={pageScrollRef}
                  embedded
                />
              </div>
            </>
          )}
      </div>
    </div>
  )
}
